import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';

interface ModuleSection {
  title: string;
  content: string[];
}


const moduleContent: ModuleSection[] = [
  {
    title: 'What is Mindful Eating?',
    content: [
      'Mindful eating means paying attention to your food, savoring each bite, and listening to your body\'s hunger and fullness cues.',
      'It encourages a positive relationship with food and can help prevent overeating.',
      // removed source
    ]
  },
  {
    title: 'Practicing Mindful Eating',
    content: [
      '• Eat slowly and without distractions.',
      '• Notice flavors, textures, and aromas.',
      '• Stop eating when you feel satisfied, not stuffed.',
      '• Reflect on how food makes you feel physically and emotionally.',
      // removed source
    ]
  },
  {
    title: 'Benefits of Mindful Eating',
    content: [
      '• Reduces overeating and emotional eating.',
      '• Improves digestion and satisfaction.',
      '• Promotes a healthy attitude toward food.',
      '• Supports weight management and overall wellness.',
      // removed source
    ]
  },
  {
    title: 'Tips to Get Started',
    content: [
      '• Set aside time for meals and avoid multitasking.',
      '• Use smaller plates to help with portion control.',
      '• Keep a food journal to track eating habits.',
      '• Practice gratitude for your food and its source.',
      // removed source
    ]
  }
];


const HealthyEatingModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('healthyEatingM3');
  const totalSteps = moduleContent.length;
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;


  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, nextStep === totalSteps - 1);
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/wellness-loop/healthy-eating-habits');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const handleFinish = async () => {
    await saveProgress(currentStep, true);
    navigate('/panel/wellness-loop/healthy-eating-habits');
  };

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  const showCompletionMessage = safeCurrentStep === totalSteps - 1;

  return (
    <div className="module-form">
      <div className="module-container">
        {/* Left Panel - Illustration */}
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Healthy Eating Module 3</h1>
            <p className="module-subtitle">Mindful Eating Practices</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Discover how mindful eating can improve your relationship with food and support healthy habits.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">{currentModule.title}</h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={safeCurrentStep === 0 ? handleFinish : handleBack}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            {safeCurrentStep < totalSteps - 1 ? (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleFinish}
              >
                Finish Module
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthyEatingModule3Page;

